import { useState } from 'react'
import { isAxiosError } from 'axios'
import { MoreHorizontal, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useRereadDealLead } from '@/hooks/use-deal-leads'
import { useScreenAgain } from '@/hooks/use-screen-again'
import { showError, showInfo } from '@/lib/toast'
import type { DealLeadDetail } from '@/types/api'

function errorCodeOf(error: unknown): string | undefined {
  if (!isAxiosError(error)) return undefined
  const data: unknown = error.response?.data
  if (typeof data !== 'object' || data === null) return undefined
  const code = (data as { errorCode?: unknown }).errorCode
  return typeof code === 'string' ? code : undefined
}

function affectedCount(error: unknown): number | undefined {
  if (!isAxiosError(error)) return undefined
  const data: unknown = error.response?.data
  if (typeof data !== 'object' || data === null) return undefined
  const details = (data as { details?: { leadsAffected?: unknown } }).details
  return typeof details?.leadsAffected === 'number'
    ? details.leadsAffected
    : undefined
}

// Re-read bills again and can overwrite reviewer corrections, so it sits behind a menu and confirms.
export function ReanalysisActions({ lead }: { lead: DealLeadDetail }) {
  const rescreen = useScreenAgain(lead.id)
  const reread = useRereadDealLead()
  const [confirming, setConfirming] = useState<{ leads: number } | null>(null)

  // Nothing to re-run while the pipeline still owns the lead.
  if (!lead.capabilities.canCorrect) return null

  const runReread = (discardCorrections: boolean) => {
    reread.mutate(
      { id: lead.id, discardCorrections },
      {
        onSuccess: (result) => {
          setConfirming(null)
          showInfo(
            result.leadsAffected === 1
              ? 'Extracting the fields again. The lead will update shortly.'
              : `Extracting the fields again. All ${result.leadsAffected} candidates from it will update.`
          )
        },
        onError: (error) => {
          // Not a failure: the server is asking whether to discard reviewer corrections.
          if (errorCodeOf(error) === 'DEAL_LEAD_HAS_CORRECTIONS') {
            setConfirming({ leads: affectedCount(error) ?? 1 })
            return
          }
          setConfirming(null)
          showError(error, 'The fields could not be extracted again.')
        },
      }
    )
  }

  const busy = rescreen.isPending || reread.isPending

  return (
    <>
      <Button
        variant="outline"
        size="badge"
        className="h-6 gap-1.25 rounded-full border-primary/55 bg-white px-2.5 text-xs text-primary [&_svg]:size-2.75"
        onClick={rescreen.run}
        disabled={busy}
        aria-busy={rescreen.isPending}
      >
        <Sparkles aria-hidden="true" className="text-secondary" />
        {rescreen.isPending ? 'Screening…' : 'Screen again'}
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="badge"
            className="size-6 rounded-full border-primary/55 bg-white p-0 text-primary [&_svg]:size-2.75"
            disabled={busy}
            aria-label="More ways to run the model again"
          >
            <MoreHorizontal aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuItem
            onSelect={() => runReread(false)}
            className="flex-col items-start"
          >
            <span>Extract the fields again</span>
            <span className="text-xs text-muted-foreground">
              Extracts the fields from the source. Costs more, and covers every
              candidate from this email.
            </span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog
        open={confirming !== null}
        onOpenChange={() => setConfirming(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace corrected fields?</AlertDialogTitle>
            <AlertDialogDescription>
              Someone has corrected fields on{' '}
              {confirming?.leads === 1
                ? 'this lead'
                : `these ${confirming?.leads} candidates`}
              . Extracting the fields again reads them fresh from the email and
              writes over those corrections. What was changed stays in the
              lead&apos;s history, but the values themselves are replaced.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep the corrections</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={() => runReread(true)}
            >
              Extract again and replace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
